import React, { useState } from 'react';
import { X, Link2, Shield, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const MeshEnrollment = ({ onClose, onLinked }) => {
  const [fingerprint, setFingerprint] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const fp = fingerprint.trim();
    if (!fp || fp.length < 16) {
      setError('Please enter a valid mesh fingerprint (at least 16 characters).');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/mesh/link-identity', { fingerprint: fp });
      onLinked();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to link identity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-virgo-navy-800 rounded-xl border border-virgo-teal-900 w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-virgo-teal-900">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-virgo-teal-400" />
            <h3 className="text-white font-semibold">Link Mesh Identity</h3>
          </div>
          <button
            onClick={onClose}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-virgo-teal-300">
            Enter your mesh node fingerprint to link it to your Virgo account.
            You can find this in:
          </p>
          <ul className="text-xs text-virgo-teal-400 space-y-1 ml-4 list-disc">
            <li><code className="text-virgo-teal-200">~/.astraea-mesh/state.json</code> (user install)</li>
            <li><code className="text-virgo-teal-200">/var/lib/astraea-mesh/meshd-state.json</code> (system install)</li>
            <li>The Virgo Browser status bar (automatically shown)</li>
          </ul>

          <div>
            <label className="block text-sm text-virgo-teal-300 mb-1">
              Mesh Fingerprint
            </label>
            <input
              type="text"
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              placeholder="e.g., a1b2c3d4e5f6..."
              className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-800 rounded-lg text-white placeholder-virgo-teal-600 focus:border-virgo-teal-500 focus:ring-1 focus:ring-virgo-teal-500 focus:outline-none font-mono text-sm"
              autoFocus
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-virgo-teal-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !fingerprint.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              <Link2 size={16} />
              {loading ? 'Linking...' : 'Link Identity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeshEnrollment;
